import React, { useState, useEffect } from 'react';
import { Dialog } from 'antd-mobile';
import { t } from '../../../../i18n';
import { AccessCode } from './AccessInfoDisplay';
import { getLocalizedAccessCodeTypeName } from '../utils/accessCodeUtils';

interface AccessCodeValidationDialogProps {
    visible: boolean;
    pendingAccessCodes: AccessCode[]; // 待验证的门禁code列表
    onValidationComplete: (results: Array<{ codeId: number; status: 'valid' | 'invalid' | 'didntTry' }>) => void;
    onClose?: () => void;
}

const AccessCodeValidationDialog: React.FC<AccessCodeValidationDialogProps> = ({
    visible,
    pendingAccessCodes,
    onValidationComplete,
    onClose
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [validationResults, setValidationResults] = useState<Array<{ codeId: number; status: 'valid' | 'invalid' | 'didntTry' }>>([]);
    const [isValidating, setIsValidating] = useState(false);

    // 当弹窗打开时，重置状态
    useEffect(() => {
        if (visible) {
            setCurrentIndex(0);
            setValidationResults([]);
        }
    }, [visible]);

    const currentCode = pendingAccessCodes[currentIndex];
    const isLastCode = currentIndex === pendingAccessCodes.length - 1;

    const handleValidation = async (status: 'valid' | 'invalid' | 'didntTry') => {
        // 防止重复点击
        if (isValidating) {
            return;
        }

        setIsValidating(true);
        const newResults = [
            ...validationResults,
            {
                codeId: currentCode.id,
                status
            }
        ];
        setValidationResults(newResults);

        if (isLastCode) {
            // 所有code都已验证，先关闭弹窗，然后在后台调用完成回调（不阻塞UI）
            if (onClose) {
                onClose();
            }
            // 后台异步调用，不阻塞UI
            Promise.resolve(onValidationComplete(newResults)).catch(error => {
                console.error('Failed to complete validation:', error);
            });
        } else {
            // 移动到下一个code
            setCurrentIndex(currentIndex + 1);
        }
        
        setIsValidating(false);
    };

    if (!visible || !currentCode) {
        return null;
    }

    // 国际化门禁类型
    const localizedType = getLocalizedAccessCodeTypeName(currentCode.type, t);

    const questionText = t('pages.delivery.orderDetail.accessCodeValidationQuestion', {
        type: localizedType,
        code: currentCode.code
    });

    return (
        <Dialog
            visible={visible}
            content={
                <div className="px-6 py-6">
                    {/* 进度指示器 - 在顶部 */}
                    {pendingAccessCodes.length > 1 && (
                        <div className="text-sm text-gray-500 mb-4">
                            {currentIndex + 1} / {pendingAccessCodes.length}
                        </div>
                    )}
                    
                    {/* 问题标题 */}
                    <div className="text-lg font-medium text-gray-900 mb-8 text-center leading-6">
                        {questionText}
                    </div>

                    {/* 三个选项 - 文字链接样式 */}
                    <div className="space-y-4">
                        <button
                            onClick={() => handleValidation('valid')}
                            disabled={isValidating}
                            className={`w-full text-base font-medium text-green-500 active:opacity-70 transition-opacity ${isValidating ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {t('pages.delivery.orderDetail.valid')}
                        </button>
                        <button
                            onClick={() => handleValidation('invalid')}
                            disabled={isValidating}
                            className={`w-full text-base font-medium text-red-500 active:opacity-70 transition-opacity ${isValidating ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {t('pages.delivery.orderDetail.invalid')}
                        </button>
                        <button
                            onClick={() => handleValidation('didntTry')}
                            disabled={isValidating}
                            className={`w-full text-base font-medium text-gray-500 active:opacity-70 transition-opacity ${isValidating ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {t('pages.delivery.orderDetail.didntTry')}
                        </button>
                    </div>

                    {/* 底部分页指示器 */}
                    {pendingAccessCodes.length > 1 && (
                        <div className="flex justify-center items-center gap-2 mt-6">
                            {pendingAccessCodes.map((_, index) => (
                                <div
                                    key={index}
                                    className={`h-1 rounded-full transition-all ${
                                        index === currentIndex
                                            ? 'w-6 bg-blue-500'
                                            : 'w-1 bg-gray-300'
                                    }`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            }
            onClose={onClose}
            closeOnAction={false}
        />
    );
};

export default AccessCodeValidationDialog;

